<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatMoney } from '@/domain/selection';
import EntrChrome from '@/ui/components/EntrChrome.vue';
import { useCardPaymentController } from './controller';
import type { CardPaymentScreenEvents, CardPaymentScreenProps } from './types';

const props = defineProps<CardPaymentScreenProps>();
const emit = defineEmits<CardPaymentScreenEvents>();
const controller = useCardPaymentController(emit);
const { t } = useI18n();

const voucherDisplay = computed<
  | { kind: 'none' }
  | { kind: 'fullyCovered' }
  | { kind: 'partial'; discount: string; due: string }
>(() => {
  const quote = props.quote;
  if (!quote || quote.prepaidAppliedCount <= 0) return { kind: 'none' };
  if (quote.amountDueCents <= 0) return { kind: 'fullyCovered' };
  return {
    kind: 'partial',
    discount: formatMoney(quote.prepaidDiscountCents, quote.currency),
    due: formatMoney(quote.amountDueCents, quote.currency),
  };
});
</script>

<template>
  <section class="screen card-screen" data-testid="screen-card-payment">
    <h1 class="t-display headline reveal" style="--reveal-order: 0">{{ t('payment.title') }}</h1>
    <p class="amount reveal" style="--reveal-order: 1">{{ amountLabel }}</p>

    <p
      v-if="voucherDisplay.kind === 'fullyCovered'"
      class="voucher-line voucher-fully-covered reveal"
      style="--reveal-order: 1"
      data-testid="voucher-fully-covered"
    >
      {{ t('payment.voucherFullyCovered') }}
    </p>
    <p
      v-else-if="voucherDisplay.kind === 'partial'"
      class="voucher-line voucher-partial reveal"
      style="--reveal-order: 1"
      data-testid="voucher-partial"
    >
      {{ t('payment.voucherPartial', { discount: voucherDisplay.discount, due: voucherDisplay.due }) }}
    </p>

    <div class="terminal-illustration reveal" style="--reveal-order: 2" aria-hidden="true">
      <div class="card" />
      <div class="terminal">
        <div class="terminal-screen" />
        <div class="reader" />
      </div>
    </div>

    <p class="waiting reveal" style="--reveal-order: 3" data-testid="payment-status">
      {{ t(state === 'waiting' ? 'payment.waiting' : 'payment.title') }}
    </p>

    <EntrChrome
      :step="3"
      can-go-back
      @back="controller.back"
      @help="controller.help"
    />
  </section>
</template>

<style scoped>
.headline {
  position: absolute;
  top: 320px;
  left: 80px;
  right: 80px;
  text-align: center;
}

.amount {
  position: absolute;
  top: 620px;
  left: 0;
  right: 0;
  color: var(--entr-text);
  font-family: var(--entr-font-body);
  font-size: 104px;
  font-weight: 700;
  line-height: 1;
  text-align: center;
}

.terminal-illustration {
  position: absolute;
  top: 860px;
  left: 50%;
  width: 320px;
  height: 380px;
  transform: translateX(-50%);
}

.card {
  position: absolute;
  top: 0;
  left: 50%;
  width: 220px;
  height: 140px;
  border: 3px solid var(--entr-text);
  border-radius: 12px;
  background: var(--entr-branding-blue);
  transform: translateX(-50%);
  animation: cardtap 1500ms ease-in-out infinite alternate;
}

.card::before {
  position: absolute;
  top: 24px;
  left: 18px;
  width: 48px;
  height: 32px;
  border-radius: 4px;
  background: var(--entr-logo-green);
  content: "";
}

.card::after {
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 16px;
  height: 8px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.45);
  content: "";
}

.terminal {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 240px;
  height: 220px;
  border: 4px solid var(--entr-text);
  border-radius: 16px;
  background: var(--entr-ink);
  transform: translateX(-50%);
}

.terminal-screen {
  position: absolute;
  top: 18px;
  left: 18px;
  right: 18px;
  height: 100px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.14);
}

.reader {
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 24px;
  height: 16px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.45);
}

.waiting {
  position: absolute;
  top: 1340px;
  left: 0;
  right: 0;
  color: var(--entr-text-soft);
  font-size: 26px;
  text-align: center;
}

.voucher-line {
  position: absolute;
  top: 760px;
  left: 64px;
  right: 64px;
  margin: 0;
  text-align: center;
  font-size: 32px;
  line-height: 1.25;
}

.voucher-fully-covered {
  color: var(--entr-logo-green);
  font-weight: 600;
}

.voucher-partial {
  color: var(--entr-text-soft);
}

@keyframes cardtap {
  from {
    transform: translateX(-50%) translateY(-16px);
  }

  to {
    transform: translateX(-50%) translateY(8px);
  }
}
</style>
